import { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue } from 'recoil';
import { toSeriesInstanceUid } from '~/domain/dicom/uids';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import { useDomainVersion } from '~/hooks/domain/useDomainVersion';
import { useFusionVersion } from '~/hooks/environment/useFusionVersion';
import { useEnvironmentViews } from '~/hooks/environment/useEnvironmentViews';
import { useResidentEnvironments } from '~/hooks/environment/useResidentEnvironments';
import { useImageStreamProgress } from '~/hooks/image/useImageStreamProgress';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import { petMipRowSeriesState } from '~/state/atoms/viewerAtoms';
import { seriesNameOf } from '~/utils/transforms/imageDataTransforms';
import { GridWidget } from '~/viewportGrid';
import { focusRowOnPetPoint, petImageOf } from '~/viewportGrid/adapters/engine';
import FittedPetMipPane from './FittedPetMipPane';
import PetMipTitleActions from './PetMipTitleActions';
import { warnWhenLocationNotShown } from './warnWhenLocationNotShown';

/** CSS px: taller than wide, as a body's projection is. */
const OPENING_SIZE = { widthPx: 220, heightPx: 360 };

/**
 * The rotatable PET projection widget over the row it is open for. It closes
 * once that row draws no PET, so a PET drawn there later does not reopen it,
 * and offers a click only while a tile shows the row.
 */
export function usePetMipWidget(): ReadonlyArray<GridWidget> {
    const intl = useIntl();
    const [rowSeriesUid, setRowSeriesUid] =
        useRecoilState(petMipRowSeriesState);
    const close = useCallback(() => setRowSeriesUid(null), [setRowSeriesUid]);
    const viewManager = useRecoilValue(viewManagerState);
    // A fuse or clear changes what the row draws without re-minting it; a
    // rename changes the series name in place.
    useFusionVersion(rowSeriesUid ?? undefined);
    useDomainVersion();
    const residents = useResidentEnvironments();
    const environment = useMemo(
        () =>
            rowSeriesUid === null
                ? undefined
                : residents.find(
                      (resident) =>
                          resident.image !== undefined &&
                          toSeriesInstanceUid(
                              resident.image.parent.seriesUid
                          ) === rowSeriesUid
                  ),
        [residents, rowSeriesUid]
    );
    const petImage = environment ? petImageOf(environment) : undefined;
    // Re-render as the PET completes, which the projection waits for.
    useImageStreamProgress(petImage);
    const seriesDescription = petImage?.seriesMetadata.seriesDescription;
    const isRowShown = useEnvironmentViews(environment).length > 0;

    useEffect(() => {
        if (rowSeriesUid !== null && (!environment || !petImage)) {
            setRowSeriesUid(null);
        }
    }, [rowSeriesUid, environment, petImage, setRowSeriesUid]);

    const locate = useCallback(
        (voxelIndex: number) => {
            if (!environment || !petImage) {
                return;
            }
            warnWhenLocationNotShown(
                focusRowOnPetPoint(
                    getDatasetEnvironmentRegistry(),
                    viewManager,
                    environment,
                    petImage.grid.indexToVoxelCenterWorldSpace(voxelIndex)
                )
            );
        },
        [environment, petImage, viewManager]
    );

    return useMemo((): GridWidget[] => {
        if (rowSeriesUid === null || !environment || !petImage) {
            return [];
        }
        return [
            {
                id: 'pet-mip',
                title: intl.formatMessage({ id: 'petMip.title' }),
                subject: seriesNameOf(
                    seriesDescription,
                    intl.formatMessage({ id: 'unknown.series' })
                ),
                hostSeriesUid: rowSeriesUid,
                openingSize: OPENING_SIZE,
                onClose: close,
                actions: <PetMipTitleActions />,
                body: () => (
                    <FittedPetMipPane
                        petImage={petImage}
                        environment={environment}
                        onLocate={isRowShown ? locate : undefined}
                    />
                ),
            },
        ];
    }, [
        intl,
        rowSeriesUid,
        environment,
        petImage,
        seriesDescription,
        isRowShown,
        locate,
        close,
    ]);
}
